import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { isEmpty, isObject } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { getODataEQFilterFrom } from '@utils/odata';
import { parseSearchHistoryToSearchResult } from '@utils/search';
import { interpolateString } from '@utils/string';
import { useAppDispatch } from '@hooks/redux';
import useActivatePipeline from '@hooks/useActivatePipeline';
import useEffectUpdateOnly from '@hooks/useEffectUpdateOnly';
import usePipelineQuery from '@hooks/usePipelineQuery';
import { useStateCleanup } from '@hooks/useStateCleanup';
import { DOCUMENTATION_LINK_LABEL } from '@constants/common';
import {
  FilterProp,
  FiltersProp,
  FilterType,
  SelectedFilterItem,
  SelectedFilters,
} from '@constants/data-table';
import { StatusCodes } from '@constants/enum/common';
import { PipelineDesiredStatusCodes, PipelineOutputType } from '@constants/pipelines';
import {
  DOCS_PROMPT_STUDIO_LINK,
  EMPTY_PROMPT_STUDIO_PAGE_SECTION,
  MIN_PROMPT_EDITOR_HEIGHT,
  PROMPT_EXPLORER_NEW_SESSION_STARTED_MESSAGE,
  PROMPT_TEMPLATES_FILTERS_FIELD_KEYS,
  PROMPT_TEMPLATES_FILTERS_TAGS_LABEL,
  PROMPT_TEMPLATES_FILTERS_USER_LABEL,
} from '@constants/prompt-explorer';
import { getPipelineMeta } from '@redux/actions/metadataFiltersActions';
import {
  fetchPipelines,
  getPipelinePrompts,
  startPollingDeployedPipelines,
  stopPollingDeployedPipelines,
} from '@redux/actions/pipelineActions';
import {
  createCustomPromptTemplate,
  deleteCustomPromptTemplate,
  getCustomPromptTemplates,
  getPromptTemplatesFilterTags,
  getPromptTemplatesFilterUser,
  getPublicPromptTemplates,
  updateCustomPromptTemplate,
  updatePipelinePrompt,
} from '@redux/actions/promptExplorerActions';
import {
  createSearchSession,
  getSearchSessions as getSearchSessionsAction,
} from '@redux/actions/searchActions';
import {
  pipelineLatestQueriesSelector,
  pipelinesSelector,
} from '@redux/selectors/pipelineSelectors';
import {
  createPromptTemplateStatusSelector,
  promptTemplatesFiltersValuesSelector,
  promptTemplatesSelector,
  promptTemplatesSortValueSelector,
  promptTemplatesStatusSelector,
  updatePromptTemplateStatusSelector,
} from '@redux/selectors/promptExplorerSelectors';
import {
  queryResultsPromptExplorerSelector,
  queryStatusPromptExplorerSelector,
} from '@redux/selectors/searchSelectors';
import { userSelector } from '@redux/selectors/userSelectors';
import {
  CustomPromptTemplate,
  IAPIPaginationData,
  IPipeline,
  IPromptNode,
  IPromptPlayground,
  ISearchResult,
  ITemplatesParams,
  MetadataFiltersValue,
  PipelineLatestQueries,
  PlaygroundId,
  PromptTemplate,
  PromptTemplatesModalTabOption,
} from '@redux/types/types';
import EmptyPageDescription from '@components/emptyPageDescription/EmptyPageDescription';
import PromptTemplatesModal from '@components/promptTemplatesModal/PromptTemplatesModal';
import PlaygroundFiltersDrawer from './components/PlaygroundFiltersDrawer';
import { RenderPromptExplorer } from './components/RenderPromptExplorer/RenderPromptExplorer';
import styles from './promptExplorer.module.scss';

const PromptStudioLandingSVG = React.lazy(() => import('@assets/empty/prompt-studio.svg?react'));

const FETCH_LIMIT = 20;
const PIPELINES_POLLING_INTERVAL = 5000;
const DEFAULT_FETCH_PIPELINES_PAYLOAD = {
  currentPage: 1,
  pageSize: 100,
  searchValue: '',
  desiredStatus: PipelineDesiredStatusCodes.DEPLOYED,
  supportsPrompt: true,
};

export type UpdatePrompt =
  | { playgroundId: string; update: Record<string, IPromptNode | unknown>; value?: undefined }
  | { playgroundId: string; value: string; update?: undefined };

interface Transformed {
  [key: string]: {
    template: unknown;
    [key: string]: unknown;
  };
}

const transform = (input: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(input).map(([key, val]) => {
      if (isObject(val) && 'text' in val) {
        const { text, ...rest } = val;
        return [key, { ...rest, template: text }];
      }
      return [key, val];
    }),
  ) as Transformed;

const PromptExplorerPage = () => {
  const dispatch = useAppDispatch();
  const { pipelineName: queryPipelineName } = useParams<{ pipelineName: string }>();

  const [promptEditorContainerExpanded, setPromptEditorContainerExpanded] = useState(false);
  const [queryValue, setQueryValue] = useState('');
  const [playgrounds, setPlaygrounds] = useState<IPromptPlayground[]>([]);
  const [displayTypingEffect, setDisplayTypingEffect] = useState<boolean>(true);
  const { data: pipelines }: IAPIPaginationData<IPipeline[]> = useSelector((state) =>
    pipelinesSelector(state, PipelineDesiredStatusCodes.DEPLOYED),
  );
  const { isLimitedUser } = useSelector(userSelector);
  const { activatePipeline, activatePipelineActionStatus, resetActivatePipelineActionStatus } =
    useActivatePipeline();

  // Querying
  const { queryPromptExplorer, resetSearchResultsPromptExplorer, addPromptExplorerInfoResult } =
    usePipelineQuery();
  const queryResults = useSelector(queryResultsPromptExplorerSelector);
  const queryStatus = useSelector(queryStatusPromptExplorerSelector);

  // Prompt Editor Height
  const [promptEditorHeight, setPromptEditorHeight] = useState(MIN_PROMPT_EDITOR_HEIGHT);
  const handlePromptEditorResize = (height: number) => {
    setPromptEditorHeight(promptEditorHeight + height);
    if (promptEditorContainerExpanded)
      setPromptEditorContainerExpanded(!promptEditorContainerExpanded);
  };

  // Sessions
  const [sessionQueriesResults, setSessionQueriesResults] = useState<
    Record<PlaygroundId, ISearchResult[]>
  >({});
  const [historyVisible, setHistoryVisible] = useState(false);
  const pipelineLatestQueries: PipelineLatestQueries = useSelector(pipelineLatestQueriesSelector);

  // Metadata filters
  const [activePlaygroundFiltersDrawer, setActivePlaygroundFiltersDrawer] = useState<string>('');

  // Prompt templates modal
  const promptTemplatesModalRef = useRef<React.ElementRef<typeof PromptTemplatesModal>>(null);
  const [activePlaygroundPromptTemplate, setActivePlaygroundPromptTemplate] = useState<string>('');
  const [activePromptTemplatesModalTab, setActivePromptTemplatesModalTab] = useState(
    PromptTemplatesModalTabOption.CUSTOM,
  );
  const [currentTemplateListPageNumber, setCurrentTemplateListPageNumber] = useState(1);
  const [selectedTemplatesFilterValuesByTab, setSelectedTemplatesFilterValuesByTab] = useState<
    Record<PromptTemplatesModalTabOption, SelectedFilters>
  >({ [PromptTemplatesModalTabOption.CUSTOM]: {}, [PromptTemplatesModalTabOption.DEEPSET]: {} });
  const [templatesSearchValue, setTemplatesSearchValue] = useState<string>('');
  const selectedPromptTemplatesSortValue = useSelector(promptTemplatesSortValueSelector);
  const promptTemplatesFiltersValues = useSelector(promptTemplatesFiltersValuesSelector);
  const promptTemplates = useSelector(promptTemplatesSelector);
  const createPromptTemplateStatus = useSelector(createPromptTemplateStatusSelector);
  const updatePromptTemplateStatus = useSelector(updatePromptTemplateStatusSelector);
  const promptTemplatesStatus = useSelector(promptTemplatesStatusSelector);

  const [initialPrompts, setInitialPrompts] = useState<string[]>([]);

  // General methods

  const getPlaygroundById = (playgroundId: string): IPromptPlayground | null =>
    playgrounds.find(({ id }) => id === playgroundId) || null;

  const updatePlaygroundValues = (playgroundId: string, values: Partial<IPromptPlayground>) => {
    setPlaygrounds((prevPlaygrounds) =>
      prevPlaygrounds.map((playground) => {
        if (playground.id !== playgroundId) return playground;
        return { ...playground, ...values };
      }),
    );
  };

  const getPipelineByName = (pipelineName: string): IPipeline | null =>
    pipelines.find(({ name }) => name === pipelineName) ?? null;

  const isSearching = () =>
    Object.values(queryStatus).some((status) => status === StatusCodes.IN_PROGRESS);

  const emptySearchResults = () =>
    Object.values(queryResults).every((result) => result.length === 0);

  useStateCleanup({
    paths: [
      'searchStore.queryResultsPromptExplorer',
      'searchStore.statusPromptExplorer',
      'searchStore.promptModalVisible',
      'searchStore.activePrompts',
    ],
  });

  useEffect(() => {
    // TODO: Add pagination on select scroll
    dispatch(fetchPipelines(DEFAULT_FETCH_PIPELINES_PAYLOAD));
    dispatch(
      getPromptTemplatesFilterTags({ promptTemplateModalActiveTab: activePromptTemplatesModalTab }),
    );
    dispatch(getPromptTemplatesFilterUser());

    // Polling - Current approach is to always poll pipelines
    // to have the latest pipelines statuses
    dispatch(
      startPollingDeployedPipelines(
        DEFAULT_FETCH_PIPELINES_PAYLOAD.currentPage,
        DEFAULT_FETCH_PIPELINES_PAYLOAD.pageSize,
        DEFAULT_FETCH_PIPELINES_PAYLOAD.searchValue,
        PIPELINES_POLLING_INTERVAL,
        DEFAULT_FETCH_PIPELINES_PAYLOAD.supportsPrompt,
      ),
    );

    return () => {
      dispatch(stopPollingDeployedPipelines());
      resetActivatePipelineActionStatus();
    };
  }, []);

  // After activating a pipeline, fetch the pipelines again
  useEffectUpdateOnly(() => {
    if (activatePipelineActionStatus === StatusCodes.SUCCESS)
      dispatch(fetchPipelines(DEFAULT_FETCH_PIPELINES_PAYLOAD));
  }, [activatePipelineActionStatus]);

  useEffect(() => {
    if (
      createPromptTemplateStatus === StatusCodes.SUCCESS ||
      updatePromptTemplateStatus === StatusCodes.SUCCESS
    )
      dispatch(getCustomPromptTemplates({}));
  }, [createPromptTemplateStatus, updatePromptTemplateStatus]);

  useEffect(() => {
    const [firstPlayground] = playgrounds;
    if (firstPlayground?.session) {
      const parsedResults = pipelineLatestQueries.data.map(parseSearchHistoryToSearchResult);
      const reversedParsedResults = parsedResults.reverse();

      setDisplayTypingEffect(false);
      setSessionQueriesResults({ [firstPlayground.id]: reversedParsedResults as ISearchResult[] });
    }
  }, [pipelineLatestQueries]);

  useEffect(() => {
    setDisplayTypingEffect(true);

    const [firstPlayground] = playgrounds;
    if (!firstPlayground?.id) return;
    const currentSessionQueriesResults = sessionQueriesResults[firstPlayground.id];

    if (currentSessionQueriesResults?.length) {
      const playgroundSearchResults = queryResults[firstPlayground.id];
      const lastPlaygroundSearchResults =
        playgroundSearchResults[playgroundSearchResults.length - 1];
      const lastSessionSearchResults =
        currentSessionQueriesResults[currentSessionQueriesResults.length - 1];

      if (lastPlaygroundSearchResults?.query_id === lastSessionSearchResults?.query_id) {
        const updatedSessionQueriesResults = currentSessionQueriesResults
          .slice(0, -1)
          .concat(lastPlaygroundSearchResults);
        setSessionQueriesResults({
          [firstPlayground.id]: updatedSessionQueriesResults,
        });
        return;
      }

      setSessionQueriesResults({
        [firstPlayground.id]: [...currentSessionQueriesResults, lastPlaygroundSearchResults],
      });
    }
  }, [queryResults]);

  useEffect(() => {
    if (activePlaygroundFiltersDrawer) {
      const playground = getPlaygroundById(activePlaygroundFiltersDrawer);
      if (!playground) return;
      dispatch(getPipelineMeta({ pipelineName: playground.pipelineName! }));
    }
  }, [activePlaygroundFiltersDrawer]);

  const getPromptTemplates = (localParams: ITemplatesParams) => {
    const getTemplates = {
      [PromptTemplatesModalTabOption.CUSTOM]: getCustomPromptTemplates,
      [PromptTemplatesModalTabOption.DEEPSET]: getPublicPromptTemplates,
    };
    const params = {
      filterValues: selectedTemplatesFilterValuesByTab[activePromptTemplatesModalTab],
      searchValue: templatesSearchValue,
      ...localParams,
    };
    dispatch(getTemplates[activePromptTemplatesModalTab](params));
  };

  const getPromptTemplatesFilters = (): FiltersProp<FilterType.MULTI_SELECT> => {
    const { tags, users } = promptTemplatesFiltersValues;

    const tagsFilter = {
      type: FilterType.MULTI_SELECT,
      key: PROMPT_TEMPLATES_FILTERS_FIELD_KEYS.TAGS_ID,
      title: PROMPT_TEMPLATES_FILTERS_TAGS_LABEL,
      options: tags,
    } as FilterProp<FilterType.MULTI_SELECT>;

    if (activePromptTemplatesModalTab === PromptTemplatesModalTabOption.CUSTOM) {
      const createdByFilter = {
        type: FilterType.MULTI_SELECT,
        key: PROMPT_TEMPLATES_FILTERS_FIELD_KEYS.USER_ID,
        title: PROMPT_TEMPLATES_FILTERS_USER_LABEL,
        style: { minWidth: '120px' },
        options: users,
      } as FilterProp<FilterType.MULTI_SELECT>;

      return [...(tags?.length ? [tagsFilter] : []), ...(users?.length ? [createdByFilter] : [])];
    }

    return [...(tags?.length ? [tagsFilter] : [])];
  };

  useEffectUpdateOnly(() => {
    setCurrentTemplateListPageNumber(1);
    dispatch(
      getPromptTemplatesFilterTags({ promptTemplateModalActiveTab: activePromptTemplatesModalTab }),
    );
    getPromptTemplates({});
  }, [activePromptTemplatesModalTab]);

  const getPipelinePromptsFromAPI = async (pipelineName: string) => {
    try {
      const resultAction = await dispatch(getPipelinePrompts(pipelineName));
      if (resultAction) {
        const content = unwrapResult(resultAction as any);
        const { prompts } = content;
        setInitialPrompts(Object.keys(prompts));
        if (!prompts || isEmpty(prompts)) return {};
        return transform(prompts);
      }
      return {};
    } catch (error) {
      return {};
    }
  };

  const getLastPromptNodeName = (prompts: Record<string, unknown>) => {
    const promptNodesNames = Object.keys(prompts) || [];
    return promptNodesNames[promptNodesNames.length - 1];
  };

  const initializePlayground = async (pipelineName?: string) => {
    const id = uuidv4();
    if (pipelineName) {
      const prompts = await getPipelinePromptsFromAPI(pipelineName);
      const lastNode = getLastPromptNodeName(prompts);

      setPlaygrounds([
        {
          id,
          pipelineName,
          prompts,
          selectedPromptNode: lastNode || '',
        },
      ]);
      return;
    }
    setPlaygrounds([{ id, prompts: {}, selectedPromptNode: '' }]);
  };

  useEffect(() => {
    initializePlayground(queryPipelineName);
  }, []);

  useEffect(() => {
    getPromptTemplates({});
  }, [selectedTemplatesFilterValuesByTab, templatesSearchValue]);

  // Session methods

  const getSearchSessions = (
    extraParams: {
      pipelineName?: string;
      limit?: number;
      pageNumber?: number;
      filter?: string;
      fetchMore?: boolean;
    } = {},
  ) => {
    const [firstPlayground] = playgrounds;
    const { pipelineName, ...restOfParams } = extraParams;

    const { pipeline_id: pipelineId } = getPipelineByName(
      pipelineName || firstPlayground.pipelineName || '',
    ) || { pipeline_id: '' };
    const params = {
      limit: FETCH_LIMIT,
      pageNumber: 1,
      filter: getODataEQFilterFrom('pipeline_id', pipelineId),
      ...restOfParams,
    };
    dispatch(getSearchSessionsAction(params));
  };

  const createSession = async (pipelineId: string) => {
    const resultAction = await dispatch(createSearchSession({ pipelineId }));
    if (resultAction) {
      getSearchSessions({ pageNumber: 1 });
      const content = unwrapResult(resultAction as any);
      return content?.search_session_id;
    }
    return null;
  };

  const onSessionSelect = (sessionId: string) => {
    const [firstPlayground] = playgrounds;
    updatePlaygroundValues(firstPlayground.id, { session: sessionId });
  };

  const openSearchHistory = () => {
    setHistoryVisible(true);
  };

  const closeSearchHistory = () => {
    setHistoryVisible(false);
  };

  // Playground methods

  const handleSelectingPipeline = async ({
    pipelineName,
    playgroundId,
  }: {
    pipelineName: string;
    playgroundId: string;
  }) => {
    setSessionQueriesResults({});
    resetSearchResultsPromptExplorer(playgroundId);
    const prompts = await getPipelinePromptsFromAPI(pipelineName);
    const lastNode = getLastPromptNodeName(prompts);

    updatePlaygroundValues(playgroundId, {
      pipelineName,
      prompts,
      selectedPromptNode: lastNode || '',
      session: null,
    });
  };

  const handleAddingNewPlayground = async (playgroundId: string, pipelineName: string) => {
    closeSearchHistory();
    setSessionQueriesResults({});
    updatePlaygroundValues(playgroundId, { session: undefined });
    const playground = getPlaygroundById(playgroundId);
    const id = uuidv4();
    setPlaygrounds((prev) => [
      ...prev,
      {
        id,
        pipelineName,
        prompts: playground?.prompts || {},
        selectedPromptNode: playground?.selectedPromptNode || '',
      },
    ]);
  };

  const handleRemovingPlayground = (playgroundId: string) => {
    resetSearchResultsPromptExplorer(playgroundId);
    setPlaygrounds((prev) => prev.filter(({ id }) => id !== playgroundId));
  };

  const onPromptEditorValueChange = ({ playgroundId, update, value }: UpdatePrompt) => {
    const index = playgrounds.findIndex((node) => node.id === playgroundId);
    if (index === -1) return;

    const pg = playgrounds[index];
    const node = pg.selectedPromptNode;

    const prompts = update ?? {
      ...pg.prompts,
      [node]: { ...(pg.prompts[node] as IPromptPlayground['prompts']), template: value },
    };

    setPlaygrounds((prev) => {
      const copy = [...prev];
      copy[index] = { ...pg, prompts };
      return copy;
    });
  };

  const onPromptEditorPromptNodeChange = ({
    node,
    playgroundId,
  }: {
    node: string;
    playgroundId: string;
  }) => {
    const playground = getPlaygroundById(playgroundId);
    if (!playground) return;
    updatePlaygroundValues(playgroundId, {
      selectedPromptNode: node,
    });
  };

  const onUpdatePipelinePrompt = (playgroundId: string) => {
    const playground = getPlaygroundById(playgroundId);
    if (!playground) return;

    const { pipelineName, prompts, selectedPromptNode } = playground;
    if (!pipelineName) return;

    dispatch(
      updatePipelinePrompt({
        pipelineName,
        prompt:
          (prompts[selectedPromptNode] as IPromptNode)?.template ?? prompts[selectedPromptNode],
        promptNode: selectedPromptNode,
      }),
    );
    return;
  };

  const handleStartingNewPlaygroundSession = (playgroundId: string) => {
    updatePlaygroundValues(playgroundId, {
      session: null,
    });
    addPromptExplorerInfoResult({
      playgroundId,
      infoText: PROMPT_EXPLORER_NEW_SESSION_STARTED_MESSAGE,
    });
  };

  const handleClearingPlayground = (playgroundId: string) => {
    resetSearchResultsPromptExplorer(playgroundId);
    setSessionQueriesResults({});
    updatePlaygroundValues(playgroundId, {
      session: null,
    });
  };

  // Prompt template methods

  const handleFetchingMoreTemplates = () => {
    const nextPage = currentTemplateListPageNumber + 1;
    setCurrentTemplateListPageNumber(nextPage);
    getPromptTemplates({ pageNumber: nextPage, fetchMore: true });
  };

  const handleSelectingPromptTemplate = (promptTemplate: PromptTemplate) => {
    const playground = getPlaygroundById(activePlaygroundPromptTemplate);
    if (!playground) return;
    const { selectedPromptNode, prompts } = playground;
    const updatedPrompts = {
      ...prompts,
      [selectedPromptNode]: {
        ...(prompts[selectedPromptNode] as IPromptNode),
        template: promptTemplate.text,
      },
    };
    updatePlaygroundValues(activePlaygroundPromptTemplate, { prompts: updatedPrompts });
  };

  const handleCreatingPromptTemplate = (promptTemplate: CustomPromptTemplate) => {
    dispatch(createCustomPromptTemplate(promptTemplate));
  };

  const handleUpdatingPromptTemplate = (
    promptTemplate: CustomPromptTemplate & { prompt_template_id: string },
  ) => {
    dispatch(updateCustomPromptTemplate(promptTemplate));
  };

  const handleDeletingPromptTemplate = (promptTemplateId: string) => {
    dispatch(deleteCustomPromptTemplate(promptTemplateId));
  };

  const handleChangingPromptTemplatesSortValue = (value: string) => {
    setCurrentTemplateListPageNumber(1);
    getPromptTemplates({ sortValue: value });
  };

  const handleSearchingPromptTemplates = (value: string) => {
    setTemplatesSearchValue(value);
  };

  const handleChangingPromptTemplatesFilters = (filterKey: string, items: SelectedFilterItem[]) => {
    const updatedValues = {
      ...selectedTemplatesFilterValuesByTab[activePromptTemplatesModalTab],
      [filterKey]: items,
    };
    setSelectedTemplatesFilterValuesByTab({
      ...selectedTemplatesFilterValuesByTab,
      [activePromptTemplatesModalTab]: updatedValues,
    });
  };

  const handleClearingAllPromptTemplatesFilters = () => {
    const filters = getPromptTemplatesFilters();
    const emptyFilterValues = filters.reduce((acc, { key }) => ({ ...acc, [key]: [] }), {});
    setSelectedTemplatesFilterValuesByTab({
      ...selectedTemplatesFilterValuesByTab,
      [activePromptTemplatesModalTab]: emptyFilterValues,
    });
  };

  // Query methods

  const getPromptTemplatesQueryParams = (prompts: Record<string, IPromptNode | unknown>) =>
    Object.keys(prompts).reduce((acc, key) => {
      const value = prompts[key];
      if (initialPrompts.includes(key)) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { template, template_name, ...rest } = prompts[key] as IPromptNode;
        void template_name;
        return {
          ...acc,
          [key]: {
            template,
            ...rest,
          },
        };
      }
      return {
        ...acc,
        [key]: value,
      };
    }, {});

  const onQuery = ({
    query,
    localFilters,
  }: { query?: string; localFilters?: Record<string, MetadataFiltersValue> } = {}) => {
    playgrounds.forEach(async (playground) => {
      const { id, pipelineName, prompts, session, filters } = playground;
      let currentSession = session;
      if (!pipelineName) return;
      const promptTemplateParams = getPromptTemplatesQueryParams(prompts);

      const { pipeline_id: pipelineId, output_type: outputType = PipelineOutputType.GENERATIVE } =
        getPipelineByName(pipelineName) || {
          pipeline_id: '',
          outputType: PipelineOutputType.GENERATIVE,
        };

      if (
        (!currentSession && playgrounds.length === 1) ||
        (!currentSession && outputType === PipelineOutputType.CHAT)
      ) {
        currentSession = await createSession(pipelineId);
        updatePlaygroundValues(id, { session: currentSession });
      }

      queryPromptExplorer({
        pipelineName,
        pipelineOutputType: outputType,
        query: query || queryValue,
        filters: localFilters || filters,
        params: promptTemplateParams,
        sessionId: currentSession!,
        playgroundId: id,
      });
    });
  };

  const onInputQueryChange = (newQuery: string) => {
    setQueryValue(newQuery);
  };

  const handleRepeatingQuery = (query: string) => {
    onInputQueryChange(query);
    onQuery({ query });
  };

  const handleSavingQueryPromptAsTemplate = (playgroundId: string, prompt: string) => {
    setActivePlaygroundPromptTemplate(playgroundId);
    promptTemplatesModalRef.current?.createCustomTemplateFromPrompt(prompt);
  };

  const handleActivatingPipeline = (playgroundId: string) => {
    const pipelineName = getPlaygroundById(playgroundId)?.pipelineName;

    if (!pipelineName) return;

    activatePipeline({ pipelineName });
  };

  // Filters methods

  const onClearAllFilters = () => {
    updatePlaygroundValues(activePlaygroundFiltersDrawer, { filters: {} });
  };

  const onClickApplyFilters = (selectedFilters: Record<string, MetadataFiltersValue>) => {
    updatePlaygroundValues(activePlaygroundFiltersDrawer, { filters: selectedFilters });
    setActivePlaygroundFiltersDrawer('');
  };

  const renderUpgradePage = () => {
    return (
      <EmptyPageDescription
        title={EMPTY_PROMPT_STUDIO_PAGE_SECTION.TITLE}
        description={EMPTY_PROMPT_STUDIO_PAGE_SECTION.DESCRIPTION.map(({ paragraph }) => ({
          paragraph: interpolateString(paragraph, {
            documentationLink: (
              <a href={DOCS_PROMPT_STUDIO_LINK} target="_blank" rel="noreferrer">
                {DOCUMENTATION_LINK_LABEL}
              </a>
            ),
          }),
        }))}
        image={<PromptStudioLandingSVG />}
        limited
      />
    );
  };

  if (isLimitedUser) return renderUpgradePage();

  return (
    <div className={styles.promptExplorerPage} translate="no">
      <PromptTemplatesModal
        ref={promptTemplatesModalRef}
        activeTab={activePromptTemplatesModalTab}
        open={!!activePlaygroundPromptTemplate}
        promptTemplates={promptTemplates.data || []}
        templateListHasMore={!!promptTemplates.has_more}
        selectedSortingValue={selectedPromptTemplatesSortValue}
        filters={getPromptTemplatesFilters()}
        selectedFilterValues={selectedTemplatesFilterValuesByTab[activePromptTemplatesModalTab]}
        fetchMoreTemplates={handleFetchingMoreTemplates}
        loadingTemplates={promptTemplatesStatus === StatusCodes.IN_PROGRESS}
        onTabChange={setActivePromptTemplatesModalTab}
        onCancel={() => setActivePlaygroundPromptTemplate('')}
        onUsePromptClick={handleSelectingPromptTemplate}
        onCreateNewCustomPrompt={handleCreatingPromptTemplate}
        onUpdateCustomPromptValues={handleUpdatingPromptTemplate}
        onDeleteCustomPrompt={handleDeletingPromptTemplate}
        onSortSelectChange={handleChangingPromptTemplatesSortValue}
        onFilterSelectChange={handleChangingPromptTemplatesFilters}
        onClearAllFilters={handleClearingAllPromptTemplatesFilters}
        onSearch={handleSearchingPromptTemplates}
      />
      {!!activePlaygroundFiltersDrawer && (
        <PlaygroundFiltersDrawer
          open={!!activePlaygroundFiltersDrawer}
          onClose={(selectedFilters) => {
            updatePlaygroundValues(activePlaygroundFiltersDrawer, {
              filters: selectedFilters,
            });
            setActivePlaygroundFiltersDrawer('');
          }}
          pipelineName={getPlaygroundById(activePlaygroundFiltersDrawer)?.pipelineName!}
          emptySearchResults={emptySearchResults()}
          activeFilters={getPlaygroundById(activePlaygroundFiltersDrawer)?.filters || {}}
          onClickApplyFilters={onClickApplyFilters}
          onClearAllFilters={onClearAllFilters}
        />
      )}
      <RenderPromptExplorer
        historyVisible={historyVisible}
        playgrounds={playgrounds}
        getPipelineByName={getPipelineByName}
        queryStatus={queryStatus}
        displayTypingEffect={displayTypingEffect}
        queryValue={queryValue}
        sessionQueriesResults={sessionQueriesResults}
        promptEditorHeight={promptEditorHeight}
        onPromptEditorValueChange={onPromptEditorValueChange}
        getPlaygroundById={getPlaygroundById}
        queryResults={queryResults}
        onPromptEditorPromptNodeChange={onPromptEditorPromptNodeChange}
        handleRemovingPlayground={handleRemovingPlayground}
        handleClearingPlayground={handleClearingPlayground}
        handleSelectingPipeline={handleSelectingPipeline}
        handleAddingNewPlayground={handleAddingNewPlayground}
        setActivePlaygroundFiltersDrawer={setActivePlaygroundFiltersDrawer}
        handleStartingNewPlaygroundSession={handleStartingNewPlaygroundSession}
        promptEditorContainerExpanded={promptEditorContainerExpanded}
        setActivePlaygroundPromptTemplate={setActivePlaygroundPromptTemplate}
        handleRepeatingQuery={handleRepeatingQuery}
        handleSavingQueryPromptAsTemplate={handleSavingQueryPromptAsTemplate}
        initialPrompts={initialPrompts}
        handlePromptEditorResize={handlePromptEditorResize}
        onUpdatePipelinePrompt={onUpdatePipelinePrompt}
        setPromptEditorContainerExpanded={setPromptEditorContainerExpanded}
        closeSearchHistory={closeSearchHistory}
        openSearchHistory={openSearchHistory}
        isSearching={isSearching}
        onInputQueryChange={onInputQueryChange}
        onQuery={onQuery}
        onSessionSelect={onSessionSelect}
        onActivatePipeline={handleActivatingPipeline}
      />
    </div>
  );
};

export default PromptExplorerPage;
